import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { Loader2, Bell, BellOff, Package, RefreshCw } from 'lucide-react';

interface LiveOrdersProps {
  floristId: string;
  limit?: number;
}

type OrderStatus = 
  | 'pending'
  | 'awaiting_florist_confirmation'
  | 'confirmed'
  | 'preparing'
  | 'ready_for_pickup'
  | 'out_for_delivery' 
  | 'delivered'
  | 'picked_up'
  | 'cancelled';

interface Order {
  id: string;
  created_at: string;
  total_amount: number;
  status: OrderStatus;
  customer_name: string;
  delivery_date: string | null;
  delivery_time: string | null;
  items_count: number;
  order_number: string | null;
  is_new?: boolean;
}

// Helper function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(amount);
};

// Helper to format date
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString('en-AU', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export function LiveOrdersPanel({ floristId, limit = 10 }: LiveOrdersProps) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [userInteracted, setUserInteracted] = useState(false);

  // Initialize audio element
  useEffect(() => {
    // We create the audio element once and reuse it.
    // Preloading helps reduce delay when play() is called.
    if (!audioRef.current) {
      const audio = new Audio('/sounds/bell-notification-337658.mp3');
      audio.preload = 'auto';
      audioRef.current = audio;
    }
  }, []);

  // Function to load orders
  const loadOrders = async () => {
    if (!floristId) {
      console.log("LiveOrdersPanel: floristId not yet available, skipping order load.");
      setOrders([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Query orders using florist_id (user ID)
      const { data: ordersData, error } = await supabase
        .from('orders')
        .select('*')
        .eq('florist_id', floristId)
        .in('status', ['pending', 'confirmed', 'preparing', 'awaiting_florist_confirmation'])
        .order('created_at', { ascending: false })
        .limit(limit);
      
      if (error) {
        console.error('Error fetching orders:', error);
        setError('Failed to load orders');
        setOrders([]);
        return;
      }
      
      if (!ordersData || ordersData.length === 0) {
        setOrders([]);
        setLastRefreshed(new Date());
        setError(null);
        return;
      }
      
      // Process the orders data
      const transformedOrders: Order[] = ordersData.map(order => ({
        id: order.id,
        created_at: order.created_at,
        total_amount: order.total_amount || 0,
        status: order.status as OrderStatus,
        customer_name: order.customer_name || order.buyer_first_name || 'Customer',
        delivery_date: order.delivery_date,
        delivery_time: order.delivery_time,
        items_count: Array.isArray(order.items) ? order.items.length : 1,
        order_number: order.order_number
      }));
      
      // Check for new orders
      const currentOrderIds = new Set(orders.map(o => o.id));
      const newOrders = transformedOrders.filter(order => !currentOrderIds.has(order.id));
      
      if (newOrders.length > 0 && orders.length > 0) {
        // Mark new orders
        newOrders.forEach(order => {
          order.is_new = true;
        });
        
        // Play sound only if enabled and user has interacted with the page
        if (soundEnabled && userInteracted && audioRef.current) {
          audioRef.current.play().catch(e => console.error('Audio play failed:', e));
        }
        
        // Show toast notification
        toast({
          title: `${newOrders.length} New Order${newOrders.length > 1 ? 's' : ''}!`,
          description: 'New orders have arrived and require your attention.',
          variant: 'default',
        });
      }
      
      setOrders(transformedOrders);
      setLastRefreshed(new Date());
      setError(null);
      
    } catch (err) {
      console.error('Error in loadOrders:', err);
      setError('Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Set up real-time subscription
  useEffect(() => {
    // Initial load
    loadOrders();
    
    // Set up real-time subscription
    let subscription;
    
    if (floristId) {
      subscription = supabase
        .channel('orders_channel')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'orders',
            filter: `florist_id=eq.${floristId}`
          },
          (payload) => {
            console.log('Received real-time update:', payload);
            loadOrders();
          }
        )
        .subscribe((status) => {
          console.log('Realtime subscription status:', status);
        });
    }

    // Auto-refresh every 30 seconds as a fallback
    const intervalId = setInterval(() => {
      loadOrders();
    }, 30000);

    // Clean up
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      clearInterval(intervalId);
    };
  }, [floristId]);

  const toggleSound = () => {
    // First interaction enables audio playback
    if (!userInteracted) {
      setUserInteracted(true);
      // Attempt to play a silent sound to "unlock" audio, a common workaround
      if (audioRef.current) {
        audioRef.current.muted = true;
        audioRef.current.play().catch(() => {}); // Ignore errors
        audioRef.current.muted = false;
      }
    }
    
    setSoundEnabled(!soundEnabled);
    toast({
      title: soundEnabled ? 'Notification Sound Disabled' : 'Notification Sound Enabled',
      variant: 'default',
    });
  };

  const handleManualRefresh = () => {
    loadOrders();
    toast({
      title: 'Refreshed',
      description: 'The orders list has been manually refreshed.',
      variant: 'default',
    });
  };

  const getStatusBadgeColor = (status: OrderStatus) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'awaiting_florist_confirmation': return 'bg-blue-100 text-blue-800 hover:bg-blue-200';
      case 'confirmed': return 'bg-purple-100 text-purple-800 hover:bg-purple-200';
      case 'preparing': return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'ready_for_pickup': return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'out_for_delivery': return 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200';
      case 'delivered': return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'picked_up': return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 hover:bg-red-200';
      default: return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  return (
    <Card className="shadow-md">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-xl font-bold">Live Orders</CardTitle>
          <CardDescription>
            Monitor incoming orders in real-time
          </CardDescription>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="icon"
            onClick={toggleSound}
            title={soundEnabled ? 'Disable notification sound' : 'Enable notification sound'}
          >
            {soundEnabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleManualRefresh}
            disabled={loading}
            title="Refresh orders"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-8">
            <Button variant="outline" onClick={loadOrders} className="mt-4">
              Try Again
            </Button>
          </div>
        )}

        {!loading && !error && orders.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="h-16 w-16 mx-auto mb-4 text-muted-foreground/40" />
            <p className="text-lg font-medium mb-2">No live orders</p>
            <p className="text-sm max-w-md mx-auto">
              When customers place orders, they'll appear here in real-time.
              You'll receive immediate notifications for new orders.
            </p>
          </div>
        )}

        {!loading && !error && orders.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} className={order.is_new ? 'bg-yellow-50' : ''}>
                    <TableCell className="font-medium">
                      {order.order_number ? `Order #${order.order_number}` : `#${order.id.substring(0, 8)}`}
                      {order.is_new && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          New
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(order.created_at)}</TableCell>
                    <TableCell>{order.customer_name}</TableCell>
                    <TableCell>{order.items_count}</TableCell>
                    <TableCell>{formatCurrency(order.total_amount)}</TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={getStatusBadgeColor(order.status)}
                      >
                        {order.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/florist-dashboard/orders/${order.id}`)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between text-xs text-muted-foreground">
        <div>
          Last refreshed: {lastRefreshed.toLocaleTimeString()}
        </div>
        <div>
          Auto-refreshes every 30 seconds
        </div>
      </CardFooter>
    </Card>
  );
} 